import React, {useState, useRef, useEffect} from 'react';
import {
  GoogleMap,
  withScriptjs,
  withGoogleMap,
  TrafficLayer,
  Marker,
  InfoWindow,
  OverlayView,
} from 'react-google-maps';

// marker to display the site office location and an info window on click
const SiteOfficeMarkerBase = ({siteOfficeLocation}) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  return (
    <Marker
      icon={{
        url: '/assets/images/officeMarker.png',
        scaledSize: new window.google.maps.Size(40, 40),
        anchor: new window.google.maps.Point(20, 20),
        scale: 0.7,
        rotation: 90,
      }}
      position={{
        lat: parseFloat(siteOfficeLocation.location.latitude),
        lng: parseFloat(siteOfficeLocation.location.longitude),
      }}
      onClick={handleToggle}
    >
      {isOpen && (
        <InfoWindow
          position={{
            lat: parseFloat(siteOfficeLocation.location.latitude),
            lng: parseFloat(siteOfficeLocation.location.longitude),
          }}
          onCloseClick={handleToggle}
        >
          <div style={{color: 'black'}} className='point-details deboard'>
            <span className='img-s'>
              <img
                className='user-image'
                src={localStorage.getItem('COMPANY_LOGO')}
              />
            </span>
            <span className='user-info'>
              <h4>{siteOfficeLocation?.officeName}</h4>
              <p>{siteOfficeLocation?.location?.locName}</p>
            </span>
          </div>
        </InfoWindow>
      )}
    </Marker>
  );
};

// Site-office markers don't change on vehicle updates (FR-021).
const SiteOfficeMarker = React.memo(SiteOfficeMarkerBase);

// "Live/running" pulse ring — shuttle-only. Injected once. A running shuttle radiates a couple of
// staggered brand-blue rings so it clearly reads as active vs. a static/idle cab marker.
if (typeof document !== 'undefined' && !document.getElementById('shuttle-pulse-style')) {
  const s = document.createElement('style');
  s.id = 'shuttle-pulse-style';
  s.textContent = `
    .shuttle-pulse-ring {
      position: absolute; left: 50%; top: 50%; width: 40px; height: 40px;
      margin: -20px 0 0 -20px; border-radius: 50%;
      background: rgba(10, 143, 220, 0.35);
      transform: scale(0.4); pointer-events: none;
      animation: shuttlePulse 1.8s ease-out infinite;
    }
    .shuttle-pulse-ring--delay { animation-delay: 0.9s; }
    @keyframes shuttlePulse {
      0%   { transform: scale(0.4); opacity: 0.85; }
      70%  { opacity: 0.12; }
      100% { transform: scale(2.6); opacity: 0; }
    }`;
  document.head.appendChild(s);
}

// Marker icons keyed by vehicle kind (car/bus) and status, so buses get the
// same colour-by-status treatment as cars and the two stay in sync.
const VEHICLE_ICONS = {
  car: {
    default: '/assets/images/male_car.svg', // active trip (male / no female)
    female: '/assets/images/female_car.svg', // active trip with female passenger
    online: '/assets/images/inactive_car.svg', // online, not on a trip
    completed: '/assets/images/available_car.svg', // trip completed
    duty: '/assets/images/blue_car.png', // duty started (blue)
  },
  bus: {
    default: '/assets/images/male_bus.svg',
    female: '/assets/images/female_bus.svg',
    online: '/assets/images/inactive_bus.svg',
    completed: '/assets/images/available_bus.svg',
    duty: '/assets/images/blue_bus.svg', // duty started (blue)
  },
};

// Overlay-based vehicle marker with rotation by heading and icon based on trip/gender
const VehicleOverlayMarkerBase = ({vehicle, isOnline = false, isDuty = false}) => {
  const deg = Number(vehicle.heading || 0) % 360;
  const size = 40; // px
  const half = size / 2;
  const isStale = vehicle?._stale === true; // greyed last-known active trip (FR-016a)
  // Server-generated (synthetic) updates are shown greyed too, like stale trips.
  const isServerGenerated = vehicle?.isServerGenerated === true;
  const isGreyed = isStale || isServerGenerated;

  const meta = vehicle?.metadata || {};
  const info = meta?.tripInfo || {};
  const flags = meta?.tripFlags || {};

  // Hover tooltip: only include the parts that actually exist so idle/duty
  // vehicles (which often lack full trip metadata) don't render "undefined".
  const vehicleNo =
    meta?.vehicleInfo?.vehicleNo || vehicle?.vehicleNo || vehicle?.vehicleNumber || vehicle?.vehicleInfo?.vehicleNo;
  const driverName = meta?.driverInfo?.driverName || vehicle?.driverName || vehicle?.driverInfo?.driverName;
  const hoverTitle = [vehicleNo, driverName ? `(${driverName})` : '']
    .filter(Boolean)
    .join(' ');

  // Determine trip status
  const rawStatus = String(vehicle?.tripStatus || info?.tripStatus || '').toUpperCase();
  const isCompleted = rawStatus === 'COMPLETED';

  // Female-car icon is driven solely by the static flag from the REST snapshot
  // (FR-025a / clarification Q5), not computed from the passenger list.
  const femalePresent = flags?.isFemaleTraveling === true || flags?.isFemaleTraveling1 === true;

  const vehicleType = (meta?.vehicleInfo?.vehicleType || '').toUpperCase();
  const isBus = vehicleType.includes('BUS');

  // Status priority (same for cars and buses): duty > online > completed > female > default
  let status = 'default';
  if (isDuty) {
    status = 'duty';
  } else if (isOnline) {
    status = 'online';
  } else if (isCompleted) {
    status = 'completed';
  } else if (femalePresent) {
    status = 'female';
  }

  // Shuttle marker uses the bus icon and, while running, stays bright + gets a live pulse ring.
  const isShuttle = info?.tripCategory === 'SHUTTLE';
  const isLiveShuttle = isShuttle && !isCompleted;
  const showBus = isBus || isShuttle;
  const iconSrc = VEHICLE_ICONS[showBus ? 'bus' : 'car'][status];
  // A running shuttle is never dimmed; a normal cab keeps the stale/server-generated greying.
  const greyed = isShuttle ? isCompleted : isGreyed;

  return (
    <OverlayView
      position={{ lat: vehicle?.latitude, lng: vehicle?.longitude }}
      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
    >
      <div style={{ position: 'absolute', left: -half, top: -half, width: size, height: size }} title={hoverTitle}>
        {isLiveShuttle && (
          <>
            <span className="shuttle-pulse-ring" />
            <span className="shuttle-pulse-ring shuttle-pulse-ring--delay" />
          </>
        )}
        <div
          style={{
            position: 'relative',
            width: size,
            height: size,
            transform: `rotate(${deg}deg)`,
            transformOrigin: 'center center',
            willChange: 'transform',
            pointerEvents: 'auto',
            // Stale active trips (FR-016a) and server-generated updates are greyed (not running shuttles)
            filter: greyed ? 'grayscale(100%)' : 'none',
            opacity: greyed ? 0.55 : 1,
          }}
        >
          <img
            src={iconSrc}
            alt={isOnline ? vehicle?.driverId : vehicle?.tripId}
            style={{ width: '100%', height: '100%', display: 'block' }}
          />
        </div>
      </div>
    </OverlayView>
  );
};

// Re-render a marker only when its own render-relevant data changes (FR-022/023).
const areVehicleMarkerPropsEqual = (prev, next) => {
  if (prev.isOnline !== next.isOnline || prev.isDuty !== next.isDuty) return false;
  const a = prev.vehicle || {};
  const b = next.vehicle || {};
  return (
    a.latitude === b.latitude &&
    a.longitude === b.longitude &&
    a.heading === b.heading &&
    a.tripStatus === b.tripStatus &&
    a._iconStatus === b._iconStatus &&
    a._isFemale === b._isFemale &&
    a._isBus === b._isBus &&
    a._stale === b._stale &&
    a.isServerGenerated === b.isServerGenerated &&
    a.tripId === b.tripId &&
    a.driverId === b.driverId
  );
};

const VehicleOverlayMarker = React.memo(VehicleOverlayMarkerBase, areVehicleMarkerPropsEqual);

// A coordinate is usable only when both lat/lng are finite, in range, and not
// the 0,0 placeholder that vehicles report before getting a GPS fix.
const isValidLatLng = (v) => {
  const lat = Number(v?.latitude);
  const lng = Number(v?.longitude);
  return (
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    Math.abs(lat) <= 90 &&
    Math.abs(lng) <= 180 &&
    !(lat === 0 && lng === 0)
  );
};

// main map component that displays the site office and vehicle markers
const Map = ({center, zoom, siteOfficeLocation, vehicles, vehiclesIdle = {}, fitAllVehicles, onFitDone}) => {
  const mapRef = useRef(null);
  const hasInitialFitRef = useRef(false);

  useEffect(() => {
    if (
      mapRef.current &&
      center &&
      Number.isFinite(center.lat) &&
      Number.isFinite(center.lng) &&
      !(center.lat === 0 && center.lng === 0)
    ) {
      mapRef.current.panTo(center);
    }
  }, [center]);

  // Initial fit to show all site offices when they first load
  useEffect(() => {
    if (hasInitialFitRef.current) return;
    const offices = Array.isArray(siteOfficeLocation) ? siteOfficeLocation : [];
    if (offices.length === 0) return;
    const map = mapRef.current;
    if (!map) return;

    const validOffices = offices.filter(o => o?.location?.latitude && o?.location?.longitude);
    if (validOffices.length === 0) return;

    const bounds = new window.google.maps.LatLngBounds();
    validOffices.forEach(o => bounds.extend(new window.google.maps.LatLng(
      parseFloat(o.location.latitude),
      parseFloat(o.location.longitude)
    )));

    if (validOffices.length === 1) {
      // Pad bounds slightly so fitBounds approximates zoom ~15 for a single office
      const center = bounds.getCenter();
      const offset = 0.005;
      bounds.extend(new window.google.maps.LatLng(center.lat() + offset, center.lng() + offset));
      bounds.extend(new window.google.maps.LatLng(center.lat() - offset, center.lng() - offset));
    }

    map.fitBounds(bounds);
    hasInitialFitRef.current = true;
  }, [siteOfficeLocation]);

  // One-shot fit of all vehicles (trip + online) when requested
  useEffect(() => {
    if (!fitAllVehicles) return;
    const map = mapRef.current;
    if (!map) return;
    const tripList = Object.values(vehicles || {});
    const idleList = Object.values(vehiclesIdle || {});
    // Only fit on vehicles that have real, in-range coordinates. Vehicles
    // without a GPS fix often carry undefined/NaN/0,0 which would otherwise
    // drag the bounds to (0,0) off Africa and make the map zoom to nowhere.
    const vehicleList = [...tripList, ...idleList].filter(
      isValidLatLng,
    );
    if (vehicleList.length < 2) {
      if (onFitDone) onFitDone();
      return;
    }
    const bounds = new window.google.maps.LatLngBounds();
    vehicleList.forEach(v => bounds.extend(new window.google.maps.LatLng(v.latitude, v.longitude)));
    // When the vehicles are clustered close together (e.g. two ongoing-trip
    // vehicles at nearly the same spot), fitBounds zooms in far too tight —
    // potentially to the max zoom. Detect a small bounds span and instead just
    // center on them at a fixed, comfortable zoom.
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    const latSpan = Math.abs(ne.lat() - sw.lat());
    const lngSpan = Math.abs(ne.lng() - sw.lng());
    const CLOSE_SPAN = 0.03; // ~3km; below this fitBounds would over-zoom
    if (latSpan < CLOSE_SPAN && lngSpan < CLOSE_SPAN) {
      // Pad the bounds to a minimum size around the cluster center so fitBounds
      // settles at a comfortable zoom instead of the max. (react-google-maps
      // does not proxy map.setZoom, so we shape the bounds instead.)
      const c = bounds.getCenter();
      const pad = 0.003; // ~330m half-size -> roughly zoom 15-16
      bounds.extend(new window.google.maps.LatLng(c.lat() + pad, c.lng() + pad));
      bounds.extend(new window.google.maps.LatLng(c.lat() - pad, c.lng() - pad));
    }
    map.fitBounds(bounds);
    if (onFitDone) onFitDone();
  }, [fitAllVehicles, vehicles, vehiclesIdle, onFitDone]);


  return (
    <GoogleMap
      ref={mapRef}
      defaultZoom={zoom}
      defaultCenter={center}
      defaultOptions={{
        streetViewControl: false,
        fullscreenControl: false,
        mapTypeControl: false,
        styles: [{}] // empty array to reset google logo colors
      }}
    >
      <TrafficLayer />
      {(Array.isArray(siteOfficeLocation) ? siteOfficeLocation : []).map((office, index) => (
        office?.location?.latitude && (
          <SiteOfficeMarker key={`site-office-${index}`} siteOfficeLocation={office} />
        )
      ))}
      {Object.values(vehicles)
        .filter((v) => typeof v?.latitude === 'number' && typeof v?.longitude === 'number')
        .map((vehicle) => (
          <VehicleOverlayMarker key={vehicle.tripId} vehicle={vehicle} isOnline={false} />
        ))}
      {Object.values(vehiclesIdle)
        .filter((v) => typeof v?.latitude === 'number' && typeof v?.longitude === 'number')
        .map((vehicle) => (
          <VehicleOverlayMarker
            key={`${vehicle.type}-${vehicle.driverId}`}
            vehicle={vehicle}
            isOnline={vehicle.type === 'online'}
            isDuty={vehicle.type === 'duty'}
          />
        ))}
    </GoogleMap>
  );
};

const WrappedMap = withScriptjs(withGoogleMap(Map));

export default WrappedMap;
